import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux';

import ProductService from '@v2/services/product.service';
import BundleHighlight from '@components/pages/shop/partial/bundle-highlight';
import Clickable from '@v2/components/common/presentational/clickables/default';
import { useModal } from '@v2/hooks/modals';
import { updateCartProducts } from '@app/store/rtk.cart.store';
import { IChild, IProduct } from "@interfaces";
import { PageContentLoader } from '@components/common/presentational/loader/page-loader';
import { findBlueprintBySize, mapVeggieBundle } from '@v2/components/pages/shop/utils';
import { packAndTrack } from '@v2/services/analytics.service';
import { imgV2 as img } from '@styles/utils';
import SubscriptionService from '@v2/services/subscription.service';
import { useAddAgeBundle } from '@v2/components/common/products/utils';
import { useIsSwapping } from '@components/common/products/utils';

import { css } from '@emotion/css'
import { tabletLandscapeUp } from '@styles/breakpoints';
import styles from '@v2/components/pages/shop/shop.styles';
import colors from '@styles/colors';
import { setSubscription } from '@app/store/rtk.subscription.store';
import { setEntities } from '@app/store/rtk.entities.store';
import { useEntity } from '@app/store/rtk.entities.selectors';
import { SwapHeader } from '../../header/header.modal';

const style = css({
    h2: {
        fontStyle: 'normal',
        fontWeight: 800,
        fontSize: 24,
        textAlign: 'center',
        letterSpacing: '0.02em',
        color: colors.NAVY,
        marginBottom: 35,

        [tabletLandscapeUp]: {
            marginBottom: 20,
            fontSize: 32,
        }
    },
    h4: {
        marginBottom: 15,
        fontStyle: 'normal',
        fontWeight: 'bold',
        fontSize: 12,
        textAlign: 'center',
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        color: colors.NAVY,
        paddingTop: 100,

        [tabletLandscapeUp]: {
            paddingTop: 70,
        }
    },

    '> div': {
        height: 'auto !important',
        '> div': {
            height: 'auto !important',
            '> div': {
                height: 'auto !important',
            },
        },
    },

    '& > .container': {
        height: 'auto !important'
    },

    '.pageContainer': {
        margin: '0 24px',
        height: '100% !important',

        '.category': {
            position: 'relative',

            '.details-wrapper': {
                '.items, .nutrients': {
                    minHeight: 'auto !important',
                    [tabletLandscapeUp]: {
                        minHeight: '255px !important',
                    }
                }
            }
        },
    },

    '.back-button': {
        position: 'absolute',
        top: '10%',
        zIndex: 1,
        left: '-20px',

        [tabletLandscapeUp]: {
            top: '40%',
            left: '-80px',
        }
    },

    '.next-button': {
        position: 'absolute',
        top: '10%',
        zIndex: 1,
        right: '-20px',

        [tabletLandscapeUp]: {
            top: '40%',
            right: '-80px',
        }
    },

    '.highlight-product': {
        flexDirection: `column-reverse !important` as any,
    },

    '.action': {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'center',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        position: 'sticky',
        bottom: 0,
        padding: 24,
        background: '#f8f5ed',
        maxWidth: 'auto',
        borderRadius: 0,
        boxShadow: '0px -2px 10px rgb(0 0 0 / 20%)',

        [tabletLandscapeUp]: {
            //position: 'unset',
        },

        button: {
            width: 'auto',
            [tabletLandscapeUp]: {
                width: 252,
            },
        }
    },

    '.veggie': {
        fontSize: 13,

        '.container-checkbox': {
            marginRight: 13,
        },

        '&.desktop': {
            display: 'none !important',
            [tabletLandscapeUp]: {
                display: 'flex !important',
            }
        },
        '&.mobile': {
            display: 'flex !important',
            [tabletLandscapeUp]: {
                display: 'none !important',
            }
        }
    },
    '.image-highlight': {
        display: 'flex !important',
    },
    '.image-highlight-wrapper': {
        order: 1,

        [tabletLandscapeUp]: {
            order: 'initial',
        }
    },
    '.action-wrapper': {
        '&.desktop': {
            display: 'none !important',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            alignContent: 'center',

            [tabletLandscapeUp]: {
                display: 'flex !important',
            }
        },

        '&.mobile': {
            display: 'flex !important',
            flexDirection: 'row !important' as any,
            justifyContent: 'space-between !important' as any,
            alignItems: 'center !important' as any,
            order: 2,
            marginBottom: '20px !important',

            [tabletLandscapeUp]: {
                display: 'none !important',
            }
        }
    },

    '.details': {
        order: 3,

        '.items': {
            order: 1
        },
        [tabletLandscapeUp]: {
            order: 'initial',

            '.items': {
                order: 'initial',
            },
        }
    },
});

function UpgradeBundleBody({ box, close, isBuyAgain }: { box: IChild, close: Function, isBuyAgain?: boolean }) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [index, setIndex] = useState(null);
    const [isVeggie, setIsVeggie] = useState((box.properties && box.properties.isVegan));
    const [animate, setAnimate] = useState(index);
    const onAddBundle = useAddAgeBundle();
    const isSwapping = useIsSwapping();

    const blueprints = useEntity('blueprints');
    const ageBundles = useEntity('ageBundles');

    const product = (index !== null && ageBundles)
        ? (isVeggie
            ? mapVeggieBundle(ageBundles[index])
            : ageBundles[index]
        )
        : null;


    const confirmAction = async () => {
        setLoading(true);
        const blueprint = findBlueprintBySize({ list: Object.values(blueprints), size: product.size });

        let newBox = Object.assign({}, box);

        // @ts-ignore
        newBox.lines = product.config;
        newBox.blueprint = blueprint
            ? blueprint
            : newBox.blueprint;

        SubscriptionService.updateUserBox(newBox as any, product.config, {
            product_id: product.id,
            is_vegan: isVeggie
        }).then((subscription) => {
            dispatch(setSubscription(subscription));
            setLoading(false);
            close();

            packAndTrack('Age Bundle Changed',
                [{ 'view': 'My Subscription Page', 'section': 'Change Bundle Modal' }, product],
                ['*', ['title', 'category', 'quantity', 'size']]);
        }).catch(console.error);
    }

    useEffect(() => {
        ProductService.list()
            .then(data => {
                dispatch(setEntities(data));
                dispatch(updateCartProducts({ data }));
                setIndex(0);

                if (data?.ageBundles && box?.properties?.productId) {
                    Object.values(data.ageBundles).forEach((product: IProduct, index: number) =>
                        product.id === Number(box.properties.productId)
                            ? setIndex(
                                data.ageBundles[index + 1]
                                    ? index + 1
                                    : index
                            )
                            : {}
                    );
                }

                setLoading(false);
            })
            .catch(console.error);
    }, []);

    useEffect(() => {
        setAnimate(index);
    }, [index])

    return (
        <>
            <SwapHeader back={() => close()} />
            <PageContentLoader loading={loading}>
                <div className={`${styles.container} ${style}`}>
                    <div className="pageContainer">
                        <div className="container">
                            <h4 className="text-center">
                                OPTIMIZED FOR YOUR LITTLE ONE
                            </h4>
                            <h2 className="text-center">
                                Choose a new age range
                            </h2>
                            <div className={`category`}>
                                {index <= 0
                                    ?
                                    <></>
                                    :
                                    <Clickable className="back-button" onClick={() => setIndex(index - 1)}>
                                        <img src={img('slider/button-arrow-left.svg')} alt="slide back" />
                                    </Clickable>
                                }
                                {index !== null &&
                                    <div className={`products highlight ${animate === index && 'animated animate__animated animate__fadeInUp-home'}`}
                                        {...{ animations: `animate__animated animate__fadeInUp-home ${index === 0 ? 'animate__delay-07s' : 'animate__delay-03s'}` }}>
                                        <BundleHighlight
                                            product={product}
                                            disabled={true}
                                            isVeggie={isVeggie}
                                            fixedHeight={true}
                                            setIsVeggie={setIsVeggie}
                                        />
                                    </div>
                                }
                                {ageBundles && Object.keys(ageBundles).length - 1 <= index
                                    ?
                                    <></>
                                    :
                                    <Clickable className="next-button" onClick={() => setIndex(index + 1)}>
                                        <img src={img('slider/button-arrow-right.svg')} alt="slide next" />
                                    </Clickable>
                                }
                            </div>
                        </div>
                    </div>
                    <div className="action">
                        {isBuyAgain
                            ?
                            <button
                                className="button primary blue"
                                disabled={product?.status === 'outofstock'}
                                onClick={() => {
                                    onAddBundle(product, isVeggie);
                                    close();
                                }} >
                                {isSwapping ? 'Add to replacements' : 'Add to cart'}
                            </button>
                            :
                            <button
                                onClick={confirmAction}
                                disabled={product?.status === 'outofstock'}
                                className="button primary blue">
                                Save Changes
                            </button>
                        }
                    </div>
                </div>
            </PageContentLoader>
        </>
    );
}

export function useBundleUpgradeModal(box: IChild, isBuyAgain?: boolean) {
    const [showBundleUpgradeModal, closeBundleUpgradeModal] = useModal({
        windowType: 'SHOP',
        alignType: 'CENTER',
        heightType: 'FULL',
        scroll: false,
        content: {
            component: () => <UpgradeBundleBody box={box} close={closeBundleUpgradeModal} isBuyAgain={isBuyAgain} />,
            props: {
                box: box,
            }
        },
        closeable: false,
        classes: [css({
            height: 'auto !important',
            background: '#F8F5ED',
            paddingBottom: 20
        })]
    });

    return [showBundleUpgradeModal]
}